import React, { useRef } from 'react';
import { Camera, ImagePlus, UserRound } from 'lucide-react';

const PRESET_COLORS = ['#fbb040', '#1f2d5a', '#4CAF50', '#E91E63', '#9C27B0', '#FF5722'];

const makeColorAvatar = (color, initial) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><circle cx="50" cy="50" r="50" fill="${color}"/><text x="50" y="62" text-anchor="middle" fill="white" font-family="sans-serif" font-size="36" font-weight="bold">${initial}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * AvatarPicker — the 3-way avatar selector (selfie / gallery / color preset).
 * Controlled component. All state lives in parent.
 */
export default function AvatarPicker({
  avatarPreview,
  onChange,
  error,
  nameInitial = 'K',
  showPicker,
  onTogglePicker,
}) {
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <input ref={fileInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={handleFile} data-testid="signup-avatar-camera" />
      <input ref={galleryInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="signup-avatar-gallery" />

      <div className="space-y-3">
        <div className="flex items-center gap-3">
          {avatarPreview ? (
            <img src={avatarPreview} alt="Avatar" className="w-14 h-14 rounded-full object-cover border-2 border-[#fbb040]" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
              <Camera className="w-6 h-6 text-gray-400" />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-black">Foto de perfil *</p>
            <p className="text-xs text-gray-400">Selecciona una opción</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex flex-col items-center gap-1.5 bg-gray-50 rounded-xl py-3 px-2 hover:bg-[#fff8e1] transition-colors border border-transparent hover:border-[#fbb040]/30"
            data-testid="signup-avatar-selfie-btn"
          >
            <Camera className="w-5 h-5 text-[#fbb040]" />
            <span className="text-[10px] font-medium text-gray-600 leading-tight text-center">Tomar Selfie</span>
          </button>
          <button
            type="button"
            onClick={() => galleryInputRef.current?.click()}
            className="flex flex-col items-center gap-1.5 bg-gray-50 rounded-xl py-3 px-2 hover:bg-[#fff8e1] transition-colors border border-transparent hover:border-[#fbb040]/30"
            data-testid="signup-avatar-gallery-btn"
          >
            <ImagePlus className="w-5 h-5 text-[#fbb040]" />
            <span className="text-[10px] font-medium text-gray-600 leading-tight text-center">Subir Galería</span>
          </button>
          <button
            type="button"
            onClick={onTogglePicker}
            className="flex flex-col items-center gap-1.5 bg-gray-50 rounded-xl py-3 px-2 hover:bg-[#fff8e1] transition-colors border border-transparent hover:border-[#fbb040]/30"
            data-testid="signup-avatar-default-btn"
          >
            <UserRound className="w-5 h-5 text-[#fbb040]" />
            <span className="text-[10px] font-medium text-gray-600 leading-tight text-center">Seleccionar Avatar</span>
          </button>
        </div>

        {showPicker && (
          <div className="grid grid-cols-6 gap-2 bg-gray-50 rounded-xl p-3">
            {PRESET_COLORS.map((color) => {
              const isSelected = avatarPreview?.includes(encodeURIComponent(color));
              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    onChange(makeColorAvatar(color, nameInitial.charAt(0).toUpperCase()));
                    onTogglePicker();
                  }}
                  className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${isSelected ? 'border-black scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                  data-testid={`avatar-color-${color.replace('#', '')}`}
                >
                  <span className="text-white text-sm font-bold">{nameInitial.charAt(0).toUpperCase()}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1 ml-1">{error}</p>}
    </div>
  );
}
